import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Button } from '../ui/button';
import { 
  Sparkles, 
  CheckCircle2, 
  Loader2, 
  X, 
  ChevronDown, 
  ChevronRight, 
  CheckSquare, 
  Square,
  AlertCircle,
  Receipt
} from 'lucide-react';

export interface ServiceTemplateCategory {
  id: string;
  category_name: string;
  sort_order?: number;
}

export interface ServiceTemplateItem {
  id: string;
  template_category_id: string;
  service_key: string;
  service_name: string;
  description?: string;
  default_unit_name?: string;
  duration_minutes?: number;
  gst_rate?: number;
  sac_code?: string;
  sac_description?: string;
}

interface ServiceTemplateActivationDrawerProps {
  tenantId: string;
  branchId: string;
  isOpen: boolean;
  onClose: () => void;
  onActivated: () => void;
}

export function ServiceTemplateActivationDrawer({
  tenantId,
  branchId,
  isOpen,
  onClose,
  onActivated
}: ServiceTemplateActivationDrawerProps) {
  const supabase = getSupabaseClient();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ServiceTemplateCategory[]>([]);
  const [templates, setTemplates] = useState<ServiceTemplateItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({});
  const [activating, setActivating] = useState(false);
  const [result, setResult] = useState<{
    services_created: number;
    services_skipped_existing: number;
    categories_created: number;
    note: string;
  } | null>(null);

  useEffect(() => {
    if (!isOpen || !supabase) return;

    async function fetchTemplates() {
      setLoading(true);
      setResult(null);
      try {
        const { data: catData, error: catErr } = await supabase
          .from('service_template_categories')
          .select('*')
          .order('sort_order', { ascending: true });
        if (catErr) throw catErr;

        const { data: tmplData, error: tmplErr } = await supabase
          .from('service_templates')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        if (tmplErr) throw tmplErr;

        setCategories(catData || []);
        setTemplates(tmplData || []);

        // Pre-select all templates by default
        const allIds = new Set<string>((tmplData || []).map((t: any) => t.id));
        setSelectedIds(allIds);

        // Expand all categories by default
        const expanded: Record<string, boolean> = {};
        (catData || []).forEach((c: any) => { expanded[c.id] = true; });
        setExpandedCats(expanded);
      } catch (err: any) {
        console.error('Error fetching templates:', err);
        toast.error(err.message || 'Failed to load service templates');
      } finally {
        setLoading(false);
      }
    }

    fetchTemplates();
  }, [isOpen, supabase]);

  if (!isOpen) return null;

  const toggleSelectAll = () => {
    if (selectedIds.size === templates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(templates.map(t => t.id)));
    }
  };

  const toggleCategorySelection = (catId: string) => {
    const catTemplates = templates.filter(t => t.template_category_id === catId);
    const allCatSelected = catTemplates.every(t => selectedIds.has(t.id));
    const next = new Set(selectedIds);

    catTemplates.forEach(t => {
      if (allCatSelected) {
        next.delete(t.id);
      } else {
        next.add(t.id);
      }
    });
    setSelectedIds(next);
  };

  const toggleItem = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const toggleCategoryExpand = (catId: string) => {
    setExpandedCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleActivate = async () => {
    if (!supabase || !tenantId) return;
    if (!branchId) {
      toast.error('Branch is required to activate services. Please configure a branch first.');
      return;
    }
    if (selectedIds.size === 0) {
      toast.error('Please select at least one service to activate.');
      return;
    }

    setActivating(true);
    try {
      const templateIdsArray = selectedIds.size === templates.length 
        ? null 
        : Array.from(selectedIds);

      const { data, error } = await supabase.rpc('activate_service_templates', {
        p_tenant_id: tenantId,
        p_branch_id: branchId,
        p_template_ids: templateIdsArray
      });

      if (error) {
        throw error;
      }

      setResult(data);
      toast.success(`Activated ${data?.services_created || 0} services! (${data?.services_skipped_existing || 0} already existed)`);
      onActivated();
    } catch (err: any) {
      console.error('Activate error:', err);
      toast.error(err.message || 'Failed to activate templates');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-end">
      <div className="bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 w-full max-w-2xl h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 text-slate-800 dark:text-slate-100">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
                <Sparkles size={18} />
              </span>
              <h2 className="text-base font-bold text-slate-900 dark:text-white">
                Ready-made Services List (Master Templates)
              </h2>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Select verified standard services to import into your catalog instantly.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X size={18} />
          </button>
        </div>

        {/* Notice Banner */}
        <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900/60 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
          <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-500" />
          <div className="space-y-0.5 leading-relaxed">
            <p className="font-semibold">Important Notes on Master Activation:</p>
            <p>• Prices are initially set to ₹0 (Quote on demand). You can customize the base price or fixed rates anytime in the Catalog.</p>
            <p>• SAC Codes & 18% GST rate are suggested industry standards. <strong>CA se verify karein</strong>.</p>
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
              <Loader2 size={24} className="animate-spin text-primary" />
              <span>Loading verified industry templates...</span>
            </div>
          ) : result ? (
            /* Result Summary Card */
            <div className="p-5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 space-y-3">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-bold text-sm">
                <CheckCircle2 size={20} />
                <span>Service Templates Successfully Processed</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center pt-2">
                <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-emerald-100 dark:border-emerald-900/50">
                  <p className="text-[10px] uppercase font-bold text-slate-400">Services Created</p>
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{result.services_created}</p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-emerald-100 dark:border-emerald-900/50">
                  <p className="text-[10px] uppercase font-bold text-slate-400">Categories Created</p>
                  <p className="text-xl font-bold text-primary mt-1">{result.categories_created}</p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-emerald-100 dark:border-emerald-900/50">
                  <p className="text-[10px] uppercase font-bold text-slate-400">Skipped (Existing)</p>
                  <p className="text-xl font-bold text-slate-500 mt-1">{result.services_skipped_existing}</p>
                </div>
              </div>
              <p className="text-xs text-emerald-800 dark:text-emerald-300 italic pt-1">
                {result.note || "Prices ₹0 par set hain — apni price daalein. SAC/GST CA se verify karein."}
              </p>
              <div className="pt-2 flex justify-end">
                <Button
                  onClick={onClose}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 px-4 rounded-xl"
                >
                  View in Catalog
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Select All Bar */}
              <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800 text-xs">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300 hover:text-primary transition-colors"
                >
                  {selectedIds.size === templates.length ? (
                    <CheckSquare size={16} className="text-primary" />
                  ) : (
                    <Square size={16} className="text-slate-400" />
                  )}
                  <span>Select All ({selectedIds.size}/{templates.length} selected)</span>
                </button>

                <span className="text-[11px] text-slate-400">
                  {categories.length} Categories • {templates.length} Services
                </span>
              </div>

              {/* Grouped by Category */}
              <div className="space-y-3">
                {categories.map(cat => {
                  const catTemplates = templates.filter(t => t.template_category_id === cat.id);
                  const isExpanded = expandedCats[cat.id] !== false;
                  const selectedInCat = catTemplates.filter(t => selectedIds.has(t.id)).length;
                  const allSelectedInCat = catTemplates.length > 0 && selectedInCat === catTemplates.length;

                  return (
                    <div
                      key={cat.id}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 overflow-hidden"
                    >
                      {/* Category Header */}
                      <div className="flex items-center justify-between p-3 bg-slate-100/70 dark:bg-slate-800/60">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCategorySelection(cat.id)}
                            className="p-0.5 text-slate-600 dark:text-slate-300 hover:text-primary"
                          >
                            {allSelectedInCat ? (
                              <CheckSquare size={15} className="text-primary" />
                            ) : selectedInCat > 0 ? (
                              <div className="w-3.5 h-3.5 rounded bg-primary/20 border border-primary flex items-center justify-center text-[10px] font-bold text-primary">
                                -
                              </div>
                            ) : (
                              <Square size={15} className="text-slate-400" />
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleCategoryExpand(cat.id)}
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200 hover:text-primary text-left"
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span>{cat.category_name}</span>
                            <span className="text-[11px] font-normal text-slate-400">
                              ({selectedInCat}/{catTemplates.length})
                            </span>
                          </button>
                        </div>
                      </div>

                      {/* Templates List */}
                      {isExpanded && (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800/60 p-1">
                          {catTemplates.map(tmpl => {
                            const isChecked = selectedIds.has(tmpl.id);
                            return (
                              <label
                                key={tmpl.id}
                                className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors text-xs ${
                                  isChecked
                                    ? 'bg-primary/5 dark:bg-primary/10'
                                    : 'hover:bg-slate-100/60 dark:hover:bg-slate-800/40'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleItem(tmpl.id)}
                                  className="mt-1 accent-primary shrink-0 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-slate-900 dark:text-white">
                                      {tmpl.service_name}
                                    </span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      {tmpl.sac_code && (
                                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono">
                                          SAC {tmpl.sac_code}
                                        </span>
                                      )}
                                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold">
                                        GST {tmpl.gst_rate ?? 18}%
                                      </span>
                                    </div>
                                  </div>
                                  {tmpl.description && (
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">
                                      {tmpl.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-1">
                                    {tmpl.default_unit_name && <span>Unit: {tmpl.default_unit_name}</span>}
                                    {tmpl.duration_minutes && <span>Duration: ~{tmpl.duration_minutes}m</span>}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        {!result && (
          <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-950">
            <span className="text-xs text-slate-500">
              <strong>{selectedIds.size}</strong> services selected to activate
            </span>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={activating}
                className="text-xs h-9"
              >
                Cancel
              </Button>

              <Button
                size="sm"
                onClick={handleActivate}
                disabled={activating || selectedIds.size === 0}
                className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-5 font-bold flex items-center gap-1.5"
              >
                {activating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Activating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    <span>Activate Selected ({selectedIds.size})</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServiceTemplateActivationDrawer;
